import { UseQueryResult } from 'react-query';
import { useMyQuery, QueryInfos, Options } from '../helpers';
import { EmptyInput, GetRideInput, GetRidesInput, GetSimilarRideInput, ListRideRecurrentInput } from '../../apiTypes';
import { typeRides } from './types';
import { getRideTrackingMinMaxTime } from '../../../utils/date';

const ridesQuery = (variables: GetRidesInput): QueryInfos => {
    return {
        name: `rides${variables?.export_format ? `-${variables?.export_format}` : ''}`,
        path: '/rides',
        type: typeRides.GET_RIDES,
        params: {
            ...variables,
        },
        ...(variables?.export_format && { responseType: 'blob' }), // To get blob as response type for download
    };
};

const specificRideQuery = (variables: GetRideInput): QueryInfos => {
    return {
        name: `ride${variables.id}`,
        path: `/rides/${variables.id}`,
        type: typeRides.GET_RIDE,
        params: {},
    };
};

const checkSimilarRideQuery = (variables: GetSimilarRideInput): QueryInfos => {
    const { riderId, ...rest } = variables;
    return {
        name: `ride${variables.riderId}${variables.pickupLat}${variables.pickupLng}${variables.dropoffLat}${variables.dropoffLng}${variables.plannedStartDate}`,
        path: `/rides/similar/${variables.riderId}`,
        type: typeRides.CHECK_SIMILAR_RIDE,
        params: { ...rest },
    };
};

const historySoonQuery = (variables: GetRidesInput): QueryInfos => {
    return {
        name: 'historySoon',
        path: '/rides',
        type: typeRides.GET_SOON_RIDES,
        params: {
            ...variables,
        },
        // Kept out of the query key: the tracking window moves at every render, which would
        // rotate the key and fire an extra request on top of the refetchInterval.
        // Evaluated at fetch time so each poll still gets a fresh window.
        dynamicParams: getRideTrackingMinMaxTime,
    };
};

export function useGetRides<T>(options?: Options<GetRidesInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, GetRidesInput>(ridesQuery, options);
}

export function useGetSoonRides<T>(options?: Options<GetRidesInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, GetRidesInput>(historySoonQuery, options);
}

export function useGetSpecificRide<T>(options?: Options<GetRideInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, GetRideInput>(specificRideQuery, options);
}

export function useGetSimilarRide<T>(options?: Options<GetSimilarRideInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, GetSimilarRideInput>(checkSimilarRideQuery, {
        staleTime: 0,
        ...options,
    });
}

// ==========================//
// =========== TAGS =========//
// ==========================//

const getTagsQuery = (): QueryInfos => {
    return {
        name: `tags`,
        path: `/tags`,
        type: typeRides.GET_TAGS,
        params: {},
    };
};

export function useGetTags<T>(options?: Options<EmptyInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, EmptyInput>(getTagsQuery, {
        staleTime: Infinity,
        ...options,
    });
}

// ====================================//
// =========== CANCEL REASONS =========//
// ====================================//

const getCancelReasonQuery = (): QueryInfos => {
    return {
        name: `cancelReasons`,
        path: `/cancel-reasons`,
        type: typeRides.GET_CANCEL_REASONS,
        params: {},
    };
};

export function useGetCancelReasons<T>(options?: Options<EmptyInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, EmptyInput>(getCancelReasonQuery, {
        staleTime: Infinity,
        ...options,
    });
}

// =================================//
// =========== RECURRRINGS =========//
// =================================//

const getRideRecurringQuery = (variables: GetRideInput): QueryInfos => {
    return {
        name: `recurrings${variables.id}`,
        path: `/recurrings/${variables.id}`,
        type: typeRides.GET_RIDE_RECURRING,
        params: {},
    };
};

const getListRideRecurringQuery = (variables: ListRideRecurrentInput): QueryInfos => {
    const { enabled = true, pagination } = variables;
    return {
        name: `recurrings`,
        path: `/recurrings`,
        type: typeRides.GET_RIDE_RECURRING,
        params: {
            enabled,
            pagination,
        },
    };
};

export function useGetRideRecurring<T>(options?: Options<GetRideInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, GetRideInput>(getRideRecurringQuery, options);
}

export function useGetListRideRecurring<T>(options?: Options<ListRideRecurrentInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, ListRideRecurrentInput>(getListRideRecurringQuery, options);
}
